/**
 * V13.21 (Itération C) — Fiche publique "Athlète du Pacifique"
 *
 * Route : /pacific-athletes/:paId
 *
 * Fiche documentaire simple : identité + chiffres clés + participations +
 * palmarès aux Jeux + sources internet.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Globe, Flag, Trophy, Medal, Calendar, MapPin, ArrowLeft, ExternalLink,
  Users, AlertCircle, Info,
} from "lucide-react";

const MEDAL_META = {
  or:     { label: "Or",     emoji: "🥇", cls: "bg-[hsl(var(--aito-gold))]/15 text-[hsl(var(--aito-gold-strong,var(--aito-gold)))] border-[hsl(var(--aito-gold))]/40" },
  argent: { label: "Argent", emoji: "🥈", cls: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-200" },
  bronze: { label: "Bronze", emoji: "🥉", cls: "bg-amber-100 text-amber-900 border-amber-300" },
};

function formatDiscipline(slug) {
  if (!slug) return "—";
  return String(slug).split("-").filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function MedalBadge({ medal, rank }) {
  const m = (medal || "").toLowerCase();
  const meta = MEDAL_META[m];
  if (meta) return <Badge className={`${meta.cls} font-semibold`}><span className="mr-1">{meta.emoji}</span>{meta.label}</Badge>;
  if (rank != null) return <span className="font-mono text-xs text-muted-foreground">#{rank}</span>;
  return <span className="text-xs text-muted-foreground">—</span>;
}

function StatKpi({ label, value, tone = "neutral" }) {
  const cls = {
    or: "text-[hsl(var(--aito-gold))]",
    argent: "text-slate-500 dark:text-slate-300",
    bronze: "text-amber-700 dark:text-amber-500",
    total: "text-[hsl(var(--aito-lagoon))]",
    participations: "text-foreground",
  }[tone] || "text-foreground";
  return (
    <div className="flex flex-col" data-testid={`pa-kpi-${tone}`}>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={`font-mono text-xl sm:text-2xl font-bold leading-none ${cls}`}>{value ?? 0}</span>
    </div>
  );
}

export default function PacificAthleteDetailPage() {
  const { paId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .get(`/pacific-athletes/${encodeURIComponent(paId)}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        setError(e?.response?.status === 404 ? "Athlète introuvable." : "Impossible de charger la fiche.");
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [paId]);

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !data?.pacific_athlete) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <AlertCircle className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
        <h1 className="font-display text-2xl font-bold mb-2">Fiche indisponible</h1>
        <p className="text-sm text-muted-foreground mb-6">{error || "Cet athlète n'a pas encore de fiche publiée."}</p>
        <Link
          to="/annuaire/athletes-du-pacifique"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--aito-lagoon))] hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour à l'annuaire
        </Link>
      </div>
    );
  }

  const pa = data.pacific_athlete;
  const stats = data.stats || {};
  const participations = data.participations || [];
  const results = data.results || [];
  const sources = data.sources || [];

  // Groupe résultats par édition
  const resultsByEdition = new Map();
  for (const r of results) {
    const ekey = r.edition_id || "unknown";
    if (!resultsByEdition.has(ekey)) resultsByEdition.set(ekey, { edition: r.edition, results: [] });
    resultsByEdition.get(ekey).results.push(r);
  }
  const resultsByEditionArr = Array.from(resultsByEdition.values())
    .sort((a, b) => (b.edition?.year || 0) - (a.edition?.year || 0));

  const initial = (pa.display_name || "?").charAt(0).toUpperCase();

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6" data-testid="pacific-athlete-detail">
      {/* Fil d'Ariane */}
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4 flex-wrap">
        <Link to="/" className="hover:underline">Accueil</Link>
        <span>/</span>
        <Link to="/annuaire/athletes-du-pacifique" className="hover:underline">Athlètes du Pacifique</Link>
        <span>/</span>
        <span className="text-foreground font-medium truncate">{pa.display_name}</span>
      </nav>

      {/* Hero identité */}
      <div className="rounded-2xl aito-grad-hero text-white p-5 sm:p-7 mb-5 overflow-hidden">
        <div className="flex items-start gap-4">
          <div className="shrink-0 h-20 w-20 rounded-full bg-white/15 border-2 border-white/30 flex items-center justify-center overflow-hidden">
            {pa.photo_url ? (
              <img src={pa.photo_url} alt={pa.display_name} className="h-full w-full object-cover" />
            ) : (
              <span className="font-display text-3xl font-bold">{initial}</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <Badge className="bg-white/15 text-white border border-white/25 mb-2">
              <Globe className="h-3 w-3 mr-1" />
              Athlète du Pacifique
            </Badge>
            <h1 className="font-display text-2xl sm:text-3xl font-bold leading-tight">
              {pa.display_name}
            </h1>
            <div className="mt-2 flex items-center gap-3 flex-wrap text-sm text-white/85">
              <span className="inline-flex items-center gap-1.5">
                <Flag className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
                {pa.delegation_name || pa.delegation_code || "Délégation inconnue"}
              </span>
              {pa.sex && (
                <span className="inline-flex items-center gap-1.5 capitalize">
                  <Users className="h-3.5 w-3.5" />
                  {pa.sex}
                </span>
              )}
              {pa.birth_year && (
                <span className="inline-flex items-center gap-1.5 font-mono text-xs">
                  <Calendar className="h-3 w-3" />
                  Né(e) en {pa.birth_year}
                </span>
              )}
            </div>
            {(pa.disciplines || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {pa.disciplines.map((d) => (
                  <Badge key={d} className="bg-white/10 text-white border border-white/20 text-xs">
                    {formatDiscipline(d)}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI */}
      <Card className="p-4 mb-5" data-testid="pa-stats">
        <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wide text-muted-foreground">
          <Trophy className="h-3.5 w-3.5 text-[hsl(var(--aito-gold))]" />
          Chiffres clés — Jeux &amp; Mini-Jeux du Pacifique
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <StatKpi label="Participations" value={stats.participations} tone="participations" />
          <StatKpi label="Or 🥇" value={stats.or} tone="or" />
          <StatKpi label="Argent 🥈" value={stats.argent} tone="argent" />
          <StatKpi label="Bronze 🥉" value={stats.bronze} tone="bronze" />
          <StatKpi label="Total podiums" value={stats.total} tone="total" />
        </div>
      </Card>

      {/* Participations */}
      {participations.length > 0 && (
        <Card className="p-4 mb-5" data-testid="pa-participations">
          <h2 className="font-display font-semibold text-base mb-3 flex items-center gap-1.5">
            <Calendar className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
            Participations aux Jeux
          </h2>
          <div className="space-y-2">
            {participations.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 p-2 rounded border bg-slate-50/60 dark:bg-slate-900/40" data-testid={`pa-participation-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm">
                    {p.edition ? (
                      <Link to={`/memoire/jeux-du-pacifique/${p.edition.key}`} className="hover:text-[hsl(var(--aito-lagoon))] hover:underline">
                        {p.edition.name}
                      </Link>
                    ) : "Édition inconnue"}
                  </div>
                  {p.edition && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      {p.edition.host_city && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {[p.edition.host_city, p.edition.host_country].filter(Boolean).join(", ")}
                        </span>
                      )}
                      <span className="font-mono">{p.edition.year}</span>
                    </div>
                  )}
                </div>
                {p.discipline && (
                  <Badge variant="outline" className="text-xs">{formatDiscipline(p.discipline)}</Badge>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Palmarès par édition */}
      {resultsByEditionArr.length > 0 && (
        <Card className="p-4 mb-5" data-testid="pa-palmares">
          <h2 className="font-display font-semibold text-base mb-3 flex items-center gap-1.5">
            <Medal className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
            Résultats &amp; palmarès aux Jeux
          </h2>
          <div className="space-y-4">
            {resultsByEditionArr.map(({ edition, results: ers }) => (
              <div key={edition?.id || "unknown"} data-testid={`pa-palmares-edition-${edition?.key || "unknown"}`}>
                <h3 className="font-display font-semibold text-sm mb-2 flex items-center gap-1.5">
                  {edition ? (
                    <Link to={`/memoire/jeux-du-pacifique/${edition.key}`} className="hover:text-[hsl(var(--aito-lagoon))] hover:underline">
                      {edition.name}
                    </Link>
                  ) : "Édition"}
                  <span className="text-xs text-muted-foreground font-mono">
                    ({ers.length} résultat{ers.length > 1 ? "s" : ""})
                  </span>
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                      <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="px-2 py-1.5">Discipline</th>
                        <th className="px-2 py-1.5">Épreuve</th>
                        <th className="px-2 py-1.5 text-center">Rang</th>
                        <th className="px-2 py-1.5">Médaille</th>
                        <th className="px-2 py-1.5">Performance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ers.map((r, idx) => (
                        <tr key={r.id || idx} className="border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-900/40">
                          <td className="px-2 py-1.5 font-medium">{formatDiscipline(r.discipline)}</td>
                          <td className="px-2 py-1.5 text-muted-foreground">{r.event_label || "—"}</td>
                          <td className="px-2 py-1.5 text-center font-mono text-xs">{r.rank != null ? `#${r.rank}` : "—"}</td>
                          <td className="px-2 py-1.5"><MedalBadge medal={r.medal} rank={r.rank} /></td>
                          <td className="px-2 py-1.5 text-xs text-muted-foreground">{r.performance || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Sources */}
      {sources.length > 0 && (
        <Card className="p-4 mb-5" data-testid="pa-sources">
          <h2 className="font-display font-semibold text-base mb-2 flex items-center gap-1.5">
            <ExternalLink className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
            Sources
          </h2>
          <ul className="space-y-1 text-xs">
            {sources.map((s, i) => (
              <li key={i}>
                <a href={s} target="_blank" rel="noreferrer noopener" className="text-[hsl(var(--aito-lagoon))] hover:underline break-all">
                  {s}
                </a>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Empty state palmarès */}
      {resultsByEditionArr.length === 0 && participations.length === 0 && (
        <Card className="p-6 text-center border-dashed">
          <Info className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Cette fiche est en cours d'enrichissement — aucun résultat n'a encore été publié pour cet athlète.
          </p>
        </Card>
      )}

      {/* Retour */}
      <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
        <Link
          to="/annuaire/athletes-du-pacifique"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--aito-lagoon))] hover:underline"
          data-testid="pa-back"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour à l'annuaire
        </Link>
        <p className="text-[11px] text-muted-foreground italic">
          Fiche documentaire — cette base n'est pas rattachée à un compte utilisateur.
        </p>
      </div>
    </div>
  );
}
